import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Shield, 
  Zap, 
  Users, 
  MapPin,
  Award,
  Heart,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Mail,
  Upload,
  HardHat,
  Briefcase,
  UserCog,
  Search
} from 'lucide-react';
import { Button } from '../components/ui/button';
import CaptchaModal from '../components/CaptchaModal';

const benefits = [
  {
    icon: Shield,
    title: 'Safety First Culture',
    description: 'Work in an environment where your safety is the top priority. Comprehensive training and equipment provided.'
  },
  {
    icon: TrendingUp,
    title: 'Career Growth',
    description: 'Clear advancement paths with ongoing training and certification opportunities to build your expertise.'
  },
  {
    icon: Heart,
    title: 'Competitive Benefits',
    description: 'Health insurance, retirement plans, and paid time off to support you and your family.'
  },
  {
    icon: Users,
    title: 'Team Environment',
    description: 'Join a supportive team of 130+ experienced professionals who collaborate and help each other succeed.'
  }
];

const positions = [
  {
    id: 'utility-locator',
    icon: HardHat,
    title: 'Underground Utility Locator',
    location: 'Georgia, Tennessee, Illinois',
    type: 'Full-time',
    schedule: 'Monday–Friday with on-call rotation',
    description: 'Utility Safeguarding LLC is seeking experienced Underground Utility Locators to join our team-oriented, safety-driven field operations. This full-time role is responsible for accurately identifying and marking underground utilities using electronic locating equipment while working outdoors in all weather conditions. We offer competitive pay, benefits, and a supportive environment where professionalism and quality work matter. Monday–Friday schedule with on-call rotation and occasional overtime. Apply now and upload your resume to be considered.',
    jobSummary: 'The Underground Utility Locator plays a critical role in damage prevention and public safety by accurately locating and marking underground utilities. This position requires technical skill, attention to detail, and strong communication while working independently in the field. Locators ensure that excavation activities are performed safely and in compliance with all applicable regulations and standards.',
    responsibilities: [
      'Read and interpret utility maps, prints, and locate requests',
      'Use electronic detection equipment to accurately locate and mark underground utilities',
      'Clearly and professionally communicate with homeowners, excavators, contractors, utility representatives, and internal teams',
      'Complete and document locate tickets using company-issued digital devices',
      'Maintain company vehicles and equipment in safe, clean, and working condition',
      'Physically troubleshoot locate issues in the field, including digging and exposing utilities when necessary to verify accuracy and complete locate requests safely and correctly',
      'Follow all company, client, state, and federal safety, quality, damage prevention, and operational guidelines and policies',
      'Work outdoors in a variety of weather and environmental conditions',
      'Participate in rotational on-call assignments, overtime, and travel as required'
    ],
    workEnvironment: [
      'Field-based role with extensive outdoor work, often in varied weather conditions and on uneven terrain',
      'Frequent walking long distances, standing for extended periods, and performing tasks in a variety of environmental conditions',
      'Drive company vehicles to and between assigned job sites based on operational needs and daily assignments',
      'Physical demands include frequently lifting up to 10 pounds, occasionally lifting up to 100 pounds, and handling heavier equipment with assistance as needed',
      'Use of company vehicles and all assigned safety equipment is mandatory',
      'Flexible schedule required, including participation in on-call rotations, overtime, and travel as needed'
    ],
    qualificationsRequired: [
      'High school diploma or equivalent required',
      'Valid driver\'s license with a clean driving record',
      'Ability to read and interpret maps, prints, and utility records',
      'Strong attention to detail and commitment to accuracy',
      'Ability to work independently with minimal supervision',
      'Comfortable using smartphones, tablets, and digital ticketing systems',
      'Strong communication and customer service skills',
      'Ability to work outdoors for extended periods in varied weather conditions',
      'Physically capable of walking long distances, bending, kneeling, and lifting equipment',
      'Willingness to work flexible schedules, including on-call rotations, overtime, and travel as required'
    ],
    preferredExperience: [
      'Previous experience as an Underground Utility Locator or in damage prevention',
      'Knowledge of underground utility systems (gas, electric, water, sewer, telecommunications, fiber, etc.)',
      'Experience using electronic locating equipment and related technologies',
      'Familiarity with One Call / 811 ticket systems',
      'Understanding of state and federal utility safety regulations and best practices',
      'Prior experience working with excavators, contractors, or utility companies',
      'Certification in utility locating or related safety training (or ability to obtain upon hire)'
    ],
    payInfo: 'Pay & Benefits – Illinois Only:\nFor positions located in the State of Illinois, the anticipated pay range for this position is $22.00–$31.00 per hour, based on relevant experience, qualifications, and other job-related factors. Illinois employees are also eligible for applicable company benefits, including medical, dental, vision, 401(k), paid leave, and other applicable benefits, subject to eligibility requirements.\nThe pay range and benefits information listed above applies specifically to this position in the State of Illinois.'
  },
  {
    id: 'utility-supervisor',
    icon: Briefcase,
    title: 'Underground Utility Supervisor',
    location: 'Georgia, Tennessee, Illinois',
    type: 'Full-time',
    schedule: 'Monday–Friday 8:00am–5:00pm, some weekends',
    description: 'The Underground Utility Locate Supervisor is responsible for supervising and leading a team of Utility Locators, ensuring safety, quality, and efficiency in underground utility detection and mapping operations. This role promotes a strong safety-first culture while overseeing daily field operations, regulatory compliance, performance management, and customer satisfaction. The Supervisor provides technical expertise, hands-on leadership, and operational coordination to support damage prevention and public safety initiatives.',
    responsibilityCategories: [
      {
        category: 'Team Leadership & Development',
        items: [
          'Supervise, mentor, and lead a team of Underground Utility Locators',
          'Promote and enforce a strong safety-first culture across all field operations',
          'Provide hands-on training, field coaching, and ongoing performance feedback',
          'Conduct performance evaluations, goal setting, and corrective action when necessary',
          'Support employee development, certification attainment, and skills advancement'
        ]
      },
      {
        category: 'Safety & Compliance',
        items: [
          'Ensure full compliance with all federal, state, client, and company damage prevention policies and regulations',
          'Promote adherence to One Call / 811 requirements and response timelines',
          'Conduct regular safety meetings, job site audits, and incident investigations',
          'Review and address safety concerns, near misses, and damage reports',
          'Reinforce safe vehicle operation, equipment handling, and field best practices'
        ]
      },
      {
        category: 'Operational Oversight',
        items: [
          'Oversee daily workflow, ticket assignments, and resource allocation to optimize efficiency',
          'Coordinate with management to balance workloads and staffing needs',
          'Ensure locate tickets are completed accurately, on time, and in accordance with quality standards',
          'Oversee documentation, digital reporting, and quality assurance checks',
          'Support damage investigations with root cause analysis and corrective planning',
          'Maintain accountability for company vehicles, locating equipment, and field tools'
        ]
      },
      {
        category: 'Technical & Field Support',
        items: [
          'Read and interpret utility maps, prints, and utility records',
          'Provide technical guidance and troubleshooting support to field staff',
          'Assist with complex or high-risk locates as needed',
          'Ensure accurate detection and marking of underground utilities including gas, electric, water, sewer, telecommunications, and fiber systems',
          'Support continuous improvement in locating techniques and technology usage'
        ]
      },
      {
        category: 'Customer & Stakeholder Relations',
        items: [
          'Serve as an escalation point for homeowners, excavators, contractors, utility representatives, and municipal partners',
          'Maintain professional communication and uphold service expectations',
          'Assist in resolving customer concerns and service issues promptly'
        ]
      },
      {
        category: 'On-Call & Field Participation',
        items: [
          'Participate in after-hours and rotational on-call assignments as required',
          'Support overtime and travel needs based on operational demand',
          'Perform field locating duties when necessary to support business needs'
        ]
      }
    ],
    workEnvironment: [
      'Primarily field-based with regular outdoor work in varied weather and terrain conditions',
      'Frequent walking, standing, bending, kneeling, and navigating uneven surfaces',
      'Ability to lift up to 25 pounds regularly and up to 100 pounds occasionally with assistance',
      'Regular travel within assigned territory using company vehicles',
      'Flexible schedule including overtime and on-call rotation participation'
    ],
    qualificationsRequired: [
      'High school diploma or equivalent',
      'Valid driver\'s license with a clean driving record',
      '3+ years of underground utility locating or damage prevention experience',
      'Prior leadership, supervisory, or team lead experience',
      'Strong knowledge of underground utility systems (gas, electric, water, sewer, telecommunications, fiber)',
      'Ability to read and interpret utility maps, prints, and records',
      'Proficiency with smartphones, tablets, and digital ticketing systems',
      'Strong organizational, problem-solving, and communication skills',
      'Demonstrated ability to lead teams while working independently',
      'Willingness to work flexible schedules including after-hours on-call rotation'
    ],
    preferredExperience: [
      'Certification in utility locating or related safety programs',
      'Experience conducting field audits, quality reviews, and damage investigations',
      'Familiarity with state and federal utility safety regulations',
      'Experience working directly with contractors, municipalities, and utility companies'
    ],
    coreCompetencies: [
      'Safety leadership and accountability',
      'Team development and mentoring',
      'Operational planning and workflow coordination',
      'Technical expertise in utility locating',
      'Quality assurance and attention to detail',
      'Conflict resolution and customer service excellence'
    ],
    payInfo: 'Pay & Benefits – Illinois Only:\nFor positions located in the State of Illinois, the anticipated pay range for this position is $75,000.00 to $85,000.00 annually, based on relevant experience, qualifications, and other job-related factors. Illinois employees are also eligible for applicable company benefits, including medical, dental, vision, 401(k), paid leave, and other applicable benefits, subject to eligibility requirements.\nThe pay range and benefits information listed above applies specifically to this position in the State of Illinois.'
  },
  {
    id: 'area-manager',
    icon: UserCog,
    title: 'Area Manager',
    location: 'Georgia, Tennessee, Illinois',
    type: 'Full-time',
    schedule: 'Monday–Friday 8:00am–5:00pm, travel may be required',
    description: 'The District Manager – Midwest Market is responsible for providing strategic and operational leadership for Utility Safeguarding LLC\'s utility locating operations throughout the Midwest region. This position has full responsibility for operational execution, customer satisfaction, financial performance, employee engagement, safety, quality, regulatory compliance, and market growth.',
    jobSummary: 'As the senior operational leader within the district, the District Manager is accountable for delivering exceptional service while ensuring every aspect of the operation aligns with Utility Safeguarding\'s core principles of Safety, Quality, Accountability, Integrity, Innovation, and Operational Excellence. This role partners closely with Executive Leadership to build a highly scalable operation capable of supporting sustained growth while consistently exceeding customer expectations and protecting underground infrastructure.',
    responsibilityCategories: [
      {
        category: 'Operational Leadership',
        items: [
          'Lead all day-to-day district operations to ensure safe, efficient, and consistent utility locating services',
          'Develop operational plans that achieve customer commitments, contractual service levels, and company performance objectives',
          'Optimize staffing, scheduling, workload distribution, and resource utilization to maximize productivity and service quality',
          'Drive continuous improvement initiatives that increase efficiency, reduce operational risk, and improve customer outcomes',
          'Ensure operational readiness for new market launches, contract mobilizations, and business expansion'
        ]
      },
      {
        category: 'Performance Management & Operational Excellence',
        items: [
          'Establish a culture built around measurable performance and accountability',
          'Monitor and improve key operational metrics: On-Time Ticket Performance (COTP), Productivity (Tickets Per Hour), Quality Assurance Scores, Damage Prevention Performance, Customer Service Levels',
          'Track Past Due Ticket Reduction, Workforce Utilization, Safety Performance, Fleet Performance, Financial Performance',
          'Develop corrective action plans, remove operational barriers, and hold leadership teams accountable for achieving established performance standards'
        ]
      },
      {
        category: 'Leadership & Organizational Development',
        items: [
          'Develop high-performing leadership teams capable of supporting Utility Safeguarding\'s continued growth',
          'Coach Operations Managers, Supervisors, Trainers, and Field Leaders',
          'Oversee Performance Management, Leadership Development, Succession Planning, Talent Identification',
          'Drive Employee Engagement and Accountability Coaching',
          'Promote a culture of professionalism, teamwork, continuous learning, and servant leadership'
        ]
      },
      {
        category: 'Customer Relationship Management',
        items: [
          'Serve as the senior operational representative for district customers',
          'Build trusted customer partnerships and lead operational review meetings',
          'Manage escalations and issue resolution, deliver performance reporting',
          'Support contract compliance and identify opportunities to improve service delivery',
          'Support customer growth initiatives and maintain strong working relationships with regulatory agencies and industry stakeholders'
        ]
      },
      {
        category: 'Financial Management',
        items: [
          'Maintain full operational accountability for district financial performance',
          'Manage Labor Productivity, Operating Expenses, Overtime, Vehicle Utilization',
          'Oversee Billing Accuracy, Dedicated Resource Billing, Large Project Billing, Footage Verification',
          'Track Revenue Capture, Budget Performance, EBITDA Objectives',
          'Partner with Executive Leadership to develop operational budgets, forecasts, staffing plans, and profitability improvement initiatives'
        ]
      },
      {
        category: 'Safety, Quality & Damage Prevention',
        items: [
          'Champion Utility Safeguarding\'s commitment to protecting employees, customers, communities, and underground infrastructure',
          'Build a proactive safety culture ensuring compliance with OSHA, state regulations, and customer requirements',
          'Lead damage investigations, conduct Root Cause Analysis, implement Corrective and Preventive Actions (CAPA)',
          'Monitor Quality Assurance programs and support field coaching and mentoring',
          'Reduce operational risk through continuous improvement and promote a "Zero Incident" mindset across all operations'
        ]
      },
      {
        category: 'Technology & Operational Innovation',
        items: [
          'Lead adoption and optimization of company technology platforms',
          'Oversee LocateOS UTTO, Fleet Management Systems, GIS Technologies',
          'Manage Performance Dashboards, Reporting & Analytics, Mobile Field Applications',
          'Partner with IT and Executive Leadership to improve automation, operational visibility, reporting accuracy, and workforce efficiency'
        ]
      },
      {
        category: 'Strategic Growth & Business Development',
        items: [
          'Support Utility Safeguarding\'s long-term strategic growth',
          'Assist with contract mobilizations and support RFP and RFI development',
          'Participate in customer presentations and identify operational efficiencies',
          'Evaluate staffing models and support expansion into new territories',
          'Develop scalable operational processes that support sustained growth',
          'Provide data-driven recommendations that improve operational performance and strengthen competitive positioning'
        ]
      }
    ],
    kpis: [
      'Customer Satisfaction and Contract Compliance',
      'On-Time Ticket Completion and Ticket Productivity (Tickets Per Hour)',
      'Past Due Ticket Reduction and Quality Assurance Performance',
      'Damage Prevention Metrics and Safety Performance',
      'Employee Retention and Leadership Development',
      'Financial Performance, Billing Accuracy, EBITDA Performance',
      'Fleet Performance, Technology Adoption, Continuous Improvement Initiatives'
    ],
    leadershipExpectations: [
      'Lead with integrity and professionalism',
      'Foster a culture of accountability, ownership, and continuous improvement',
      'Make data-driven operational decisions',
      'Develop future leaders through coaching and mentorship',
      'Communicate effectively across all levels of the organization',
      'Build strong customer partnerships based on trust and transparency',
      'Promote collaboration across Operations, Safety, Quality, IT, Finance, HR, and Executive Leadership',
      'Consistently demonstrate Utility Safeguarding\'s Core Values in every decision and interaction'
    ],
    positionImpact: 'The District Manager plays a critical leadership role in advancing Utility Safeguarding\'s mission to deliver industry-leading utility damage prevention services through operational excellence, innovation, and exceptional customer service. Success in this role will be measured not only by operational and financial results, but by the ability to build a disciplined, scalable organization that develops people, strengthens customer partnerships, protects underground infrastructure, and supports the company\'s long-term strategic growth.',
    payInfo: 'Pay & Benefits – Illinois Only:\nFor positions located in the State of Illinois, the anticipated pay range for this position is $85,000.00 to $110,000.00 annually, based on relevant experience, qualifications, and other job-related factors. Illinois employees are also eligible for applicable company benefits, including medical, dental, vision, 401(k), paid leave, and other applicable benefits, subject to eligibility requirements.\nThe pay range and benefits information listed above applies specifically to this position in the State of Illinois.'
  },
  {
    id: 'damage-investigator',
    icon: Search,
    title: 'Damage Investigator',
    location: 'Georgia, Tennessee',
    type: 'Non-Exempt / Hourly',
    schedule: 'Schedule varies based on operational needs',
    description: 'The Damage Investigator is an hourly, field-based position responsible for supporting damage prevention, quality assurance, and operational compliance. The position conducts utility damage investigations, performs routine field audits of technicians and completed locate work, documents findings, and communicates quality or compliance concerns to supervision and management.',
    jobSummary: 'The Damage Investigator works closely with Operations, Quality, Compliance, and Training to help improve field performance, reduce preventable damages, and verify that company procedures and customer requirements are being followed.',
    responsibilityCategories: [
      {
        category: 'Damage Investigation',
        items: [
          'Investigate assigned utility damages and related field incidents',
          'Visit damage locations and work sites as needed to gather facts and document conditions',
          'Gather photographs, ticket information, field documentation, employee statements, and other supporting information',
          'Review locate markings, ticket scope, documentation, and field conditions',
          'Complete investigation reports accurately and within established timelines',
          'Review applicable 811 tickets, revisions, positive responses, and locate documentation',
          'Inspect damage and excavation locations and document site conditions',
          'Photograph markings, excavation areas, facilities, and other relevant conditions',
          'Speak with technicians, supervisors, contractors, excavators, or other involved parties',
          'Review GPS, mapping, UTTO, or other available field information',
          'Establish a basic timeline of events and document factual observations',
          'Identify potential contributing factors and submit findings for management review'
        ]
      },
      {
        category: 'Field Quality Audits',
        items: [
          'Conduct field quality audits each workday throughout assigned operating areas',
          'Observe technicians performing locate work and review completed work for accuracy and compliance',
          'Verify technicians are following company procedures, safety requirements, and customer expectations',
          'Review use of required locating equipment, documentation systems, and company technology',
          'Identify and document quality, safety, documentation, or compliance concerns',
          'Observe technicians performing active locates',
          'Review completed locate markings for accuracy and completeness',
          'Verify ticket scope and work-area coverage',
          'Review required photographs and supporting documentation',
          'Confirm proper use of locating equipment and company-required technology',
          'Identify improper locating practices or documentation deficiencies'
        ]
      },
      {
        category: 'Communication & Follow-Up',
        items: [
          'Communicate significant findings to supervisors, Quality, Compliance, and Operations management',
          'Assist with identifying technicians who may require additional coaching, training, field observation, or recertification',
          'Participate in damage review meetings and follow-up field observations when requested',
          'Maintain accurate and organized electronic and field records',
          'Identify recurring field issues and communicate trends to management',
          'Support damage prevention, quality improvement, and other operational field activities as assigned',
          'Review technicians with recent damages, failed audits, or other quality concerns',
          'Conduct follow-up audits after training or corrective action',
          'Document both compliant work and areas requiring improvement'
        ]
      }
    ],
    workEnvironment: [
      'Primarily field-based with regular driving throughout assigned operating areas',
      'Work is performed outdoors in varying weather conditions and around active construction and excavation sites',
      'Frequent walking, standing, entering and exiting a vehicle, and movement across uneven terrain may be required',
      'Ability to carry and operate standard field equipment and mobile technology',
      'Local and regional travel required',
      'Overtime may be required based on workload and business needs'
    ],
    qualificationsRequired: [
      'Ability to read and understand 811 tickets, maps, and field documentation',
      'Ability to work independently in a field environment',
      'Strong attention to detail and ability to document findings accurately',
      'Good written and verbal communication skills',
      'Basic computer and mobile-device skills, including use of company applications and electronic reporting systems',
      'Ability to manage multiple assignments and deadlines',
      'Valid driver\'s license and acceptable driving record'
    ],
    preferredExperience: [
      'Utility locating, underground construction, quality, damage prevention, or related field experience preferred',
      'General knowledge of underground utility locating practices preferred'
    ],
    payInfo: 'This is a Non-Exempt / Hourly position. Competitive pay based on experience and qualifications. Schedule may vary based on operational needs, damage investigations, and customer requirements.'
  }
];

const values = [
  'Precision in everything we do',
  'Zero-incident safety mindset',
  'Technology-driven efficiency',
  'Continuous improvement',
  'Community and asset protection',
  'Regulatory compliance excellence'
];

export default function CareersPage() {
  const [isCaptchaOpen, setIsCaptchaOpen] = useState(false);

  const handleApplyClick = (e) => {
    e.preventDefault();
    setIsCaptchaOpen(true);
  };

  return (
    <div className="bg-skytec-bg pt-20">
      {/* Captcha Modal */}
      <CaptchaModal isOpen={isCaptchaOpen} onClose={() => setIsCaptchaOpen(false)} />

      {/* Hero Section */}
      <section className="py-24 relative overflow-hidden" data-testid="careers-hero">
        <div className="absolute inset-0 scan-grid opacity-20" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="text-skytec-primary font-mono text-sm tracking-widest uppercase">Join Our Team</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-skytec-fg mt-4 mb-6 uppercase tracking-tight">
              CAREERS AT <span className="neon-text">USG</span>
            </h1>
            <p className="text-skytec-fg/60 max-w-3xl mx-auto text-lg mb-8">
              Build a career protecting critical infrastructure. Join 130+ experienced professionals 
              dedicated to precision, safety, and technological excellence.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                size="lg"
                className="bg-skytec-secondary text-white hover:bg-skytec-secondary/90 font-heading font-bold"
                data-testid="careers-apply-btn"
                onClick={handleApplyClick}
              >
                <Upload className="w-5 h-5 mr-2" />
                APPLY NOW
              </Button>
            </div>
            <p className="text-skytec-fg/50 text-sm mt-4">
              Questions about opportunities? Email us at{' '}
              <a href="mailto:Recruiting@usgllc811.com" className="text-skytec-primary hover:underline">
                Recruiting@usgllc811.com
              </a>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-24 bg-skytec-surface border-y border-skytec-border" data-testid="why-work-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-skytec-primary font-mono text-sm tracking-widest uppercase">Why USG</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-skytec-fg mt-4 uppercase tracking-tight">
              WHY WORK WITH US
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-6 bg-skytec-bg rounded-xl border border-skytec-border"
              >
                <div className="w-14 h-14 bg-skytec-primary-dim rounded-full flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-7 h-7 text-skytec-primary" />
                </div>
                <h3 className="font-heading font-bold text-skytec-fg mb-2">{benefit.title}</h3>
                <p className="text-skytec-fg/60 text-sm">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-24" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-skytec-primary font-mono text-sm tracking-widest uppercase">Our Culture</span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-skytec-fg mt-4 mb-6 uppercase tracking-tight">
                BUILT ON THREE PILLARS
              </h2>
              <div className="space-y-6">
                <div className="p-4 bg-skytec-surface rounded-lg border border-skytec-border">
                  <h3 className="font-heading font-bold text-skytec-primary mb-2">PRECISION</h3>
                  <p className="text-skytec-fg/60 text-sm">Accurate, verified locating with AI-supported quality checks</p>
                </div>
                <div className="p-4 bg-skytec-surface rounded-lg border border-skytec-border">
                  <h3 className="font-heading font-bold text-skytec-secondary mb-2">PROTECTION</h3>
                  <p className="text-skytec-fg/60 text-sm">Zero-incident mindset, community & asset safety, regulatory compliance</p>
                </div>
                <div className="p-4 bg-skytec-surface rounded-lg border border-skytec-border">
                  <h3 className="font-heading font-bold text-skytec-accent mb-2">PROGRESS</h3>
                  <p className="text-skytec-fg/60 text-sm">Technology-driven efficiency, continuous improvement, faster project delivery</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-skytec-surface rounded-2xl border border-skytec-border p-8">
                <h3 className="font-heading text-xl font-bold text-skytec-fg mb-6 uppercase">What We Value</h3>
                <div className="space-y-4">
                  {values.map((value, index) => (
                    <div key={index} className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-skytec-accent mr-3 flex-shrink-0" />
                      <span className="text-skytec-fg/80">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-24 bg-skytec-surface border-y border-skytec-border" data-testid="positions-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-skytec-primary font-mono text-sm tracking-widest uppercase">Opportunities</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-skytec-fg mt-4 uppercase tracking-tight">
              OPEN POSITIONS
            </h2>
            <p className="text-skytec-fg/60 mt-4 max-w-2xl mx-auto">
              We're looking for dedicated professionals to join our growing team. 
              Submit your resume to be considered for the following roles.
            </p>
          </motion.div>

          <div className="space-y-8">
            {positions.map((position, index) => (
              <motion.div
                key={position.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-skytec-bg rounded-2xl border border-skytec-border p-8 hover:border-skytec-primary/30 transition-all"
                data-testid={`position-${position.id}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-skytec-primary-dim rounded-xl flex items-center justify-center">
                      <position.icon className="w-8 h-8 text-skytec-primary" />
                    </div>
                  </div>
                  <div className="flex-grow">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                      <div>
                        <h3 className="font-heading text-xl font-bold text-skytec-fg">{position.title}</h3>
                        <div className="flex flex-wrap items-center gap-4 mt-2 text-sm">
                          <span className="flex items-center text-skytec-fg/60">
                            <MapPin className="w-4 h-4 mr-1" />
                            {position.location}
                          </span>
                          <span className="px-2 py-1 bg-skytec-primary-dim text-skytec-primary rounded text-xs font-medium">
                            {position.type}
                          </span>
                          <span className="text-skytec-fg/50 text-xs">
                            {position.schedule}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Description */}
                    <p className="text-skytec-fg/70 text-sm leading-relaxed mb-4">
                      {position.description}
                    </p>
                    
                    {/* Job Summary (if exists) */}
                    {position.jobSummary && (
                      <p className="text-skytec-fg/70 text-sm leading-relaxed mb-6 italic">
                        {position.jobSummary}
                      </p>
                    )}
                    
                    {/* Key Responsibilities - Simple list format */}
                    {position.responsibilities && (
                      <div className="mb-6">
                        <h4 className="font-heading font-semibold text-skytec-fg mb-3 text-sm uppercase tracking-wide">Key Responsibilities</h4>
                        <ul className="grid md:grid-cols-2 gap-2">
                          {position.responsibilities.map((item, idx) => (
                            <li key={idx} className="flex items-start text-skytec-fg/70 text-sm">
                              <CheckCircle className="w-4 h-4 text-skytec-accent mr-2 mt-0.5 flex-shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Key Responsibilities - Categorized format */}
                    {position.responsibilityCategories && (
                      <div className="mb-6">
                        <h4 className="font-heading font-semibold text-skytec-fg mb-4 text-sm uppercase tracking-wide">Key Responsibilities</h4>
                        <div className="space-y-4">
                          {position.responsibilityCategories.map((cat, catIdx) => (
                            <div key={catIdx} className="bg-skytec-surface/50 rounded-lg p-4 border border-skytec-border/50">
                              <h5 className="font-heading font-medium text-skytec-primary mb-2 text-sm">{cat.category}</h5>
                              <ul className="space-y-1">
                                {cat.items.map((item, idx) => (
                                  <li key={idx} className="flex items-start text-skytec-fg/70 text-sm">
                                    <CheckCircle className="w-4 h-4 text-skytec-accent mr-2 mt-0.5 flex-shrink-0" />
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Work Environment & Physical Requirements */}
                    {position.workEnvironment && (
                      <div className="mb-6">
                        <h4 className="font-heading font-semibold text-skytec-fg mb-3 text-sm uppercase tracking-wide">Work Environment & Physical Requirements</h4>
                        <ul className="grid md:grid-cols-2 gap-2">
                          {position.workEnvironment.map((item, idx) => (
                            <li key={idx} className="flex items-start text-skytec-fg/70 text-sm">
                              <Shield className="w-4 h-4 text-skytec-secondary mr-2 mt-0.5 flex-shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Qualifications Required */}
                    {position.qualificationsRequired && (
                      <div className="mb-6">
                        <h4 className="font-heading font-semibold text-skytec-fg mb-3 text-sm uppercase tracking-wide">Qualifications Required</h4>
                        <ul className="grid md:grid-cols-2 gap-2">
                          {position.qualificationsRequired.map((item, idx) => (
                            <li key={idx} className="flex items-start text-skytec-fg/70 text-sm">
                              <Award className="w-4 h-4 text-skytec-primary mr-2 mt-0.5 flex-shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Preferred Experience */}
                    {position.preferredExperience && (
                      <div className="mb-6">
                        <h4 className="font-heading font-semibold text-skytec-fg mb-3 text-sm uppercase tracking-wide">Preferred Experience</h4>
                        <ul className="grid md:grid-cols-2 gap-2">
                          {position.preferredExperience.map((item, idx) => (
                            <li key={idx} className="flex items-start text-skytec-fg/70 text-sm">
                              <TrendingUp className="w-4 h-4 text-skytec-accent mr-2 mt-0.5 flex-shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Core Competencies (for Supervisor) */}
                    {position.coreCompetencies && (
                      <div className="mb-6">
                        <h4 className="font-heading font-semibold text-skytec-fg mb-3 text-sm uppercase tracking-wide">Core Competencies</h4>
                        <ul className="grid md:grid-cols-2 gap-2">
                          {position.coreCompetencies.map((item, idx) => (
                            <li key={idx} className="flex items-start text-skytec-fg/70 text-sm">
                              <Zap className="w-4 h-4 text-skytec-primary mr-2 mt-0.5 flex-shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* KPIs (for Area Manager) */}
                    {position.kpis && (
                      <div className="mb-6">
                        <h4 className="font-heading font-semibold text-skytec-fg mb-3 text-sm uppercase tracking-wide">Key Performance Indicators (KPIs)</h4>
                        <ul className="grid md:grid-cols-2 gap-2">
                          {position.kpis.map((item, idx) => (
                            <li key={idx} className="flex items-start text-skytec-fg/70 text-sm">
                              <TrendingUp className="w-4 h-4 text-skytec-accent mr-2 mt-0.5 flex-shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Leadership Expectations (for Area Manager) */}
                    {position.leadershipExpectations && (
                      <div className="mb-6">
                        <h4 className="font-heading font-semibold text-skytec-fg mb-3 text-sm uppercase tracking-wide">Leadership Expectations</h4>
                        <ul className="grid md:grid-cols-2 gap-2">
                          {position.leadershipExpectations.map((item, idx) => (
                            <li key={idx} className="flex items-start text-skytec-fg/70 text-sm">
                              <Users className="w-4 h-4 text-skytec-primary mr-2 mt-0.5 flex-shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Position Impact (for Area Manager) */}
                    {position.positionImpact && (
                      <div className="mb-6 p-4 bg-skytec-primary/5 rounded-lg border border-skytec-primary/20">
                        <h4 className="font-heading font-semibold text-skytec-primary mb-2 text-sm uppercase tracking-wide">Position Impact</h4>
                        <p className="text-skytec-fg/70 text-sm leading-relaxed">{position.positionImpact}</p>
                      </div>
                    )}

                    {/* Pay & Benefits */}
                    <div className="p-4 bg-skytec-surface rounded-lg border border-skytec-border mb-6">
                      <h4 className="font-heading font-semibold text-skytec-secondary mb-2 text-sm">Pay & Benefits</h4>
                      <p className="text-skytec-fg/70 text-sm whitespace-pre-line leading-relaxed">{position.payInfo}</p>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button 
                        className="bg-skytec-secondary text-white hover:bg-skytec-secondary/90 font-heading"
                        onClick={handleApplyClick}
                        data-testid={`apply-btn-${position.id}`}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Apply Now
                      </Button>
                      <a href={`mailto:Recruiting@usgllc811.com?subject=Question about ${position.title}`}>
                        <Button 
                          variant="outline"
                          className="border-skytec-border text-skytec-fg hover:bg-skytec-surface"
                        >
                          <Mail className="w-4 h-4 mr-2" />
                          Ask a Question
                        </Button>
                      </a>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* General Application CTA */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-16 p-8 bg-gradient-to-r from-skytec-primary/10 to-skytec-secondary/10 rounded-2xl border border-skytec-border text-center"
          >
            <h3 className="font-heading text-xl font-bold text-skytec-fg mb-3">
              Don't see the right position?
            </h3>
            <p className="text-skytec-fg/60 mb-6 max-w-xl mx-auto">
              We're always looking for talented individuals to join our team. 
              Fill out our application form and we'll keep you in mind for future opportunities.
            </p>
            <Button 
              size="lg"
              className="bg-skytec-primary text-skytec-bg hover:bg-skytec-primary/90 font-heading"
              onClick={handleApplyClick}
            >
              <Upload className="w-5 h-5 mr-2" />
              APPLY NOW
            </Button>
            <p className="text-skytec-fg/50 text-sm mt-4">
              Questions? Email: <a href="mailto:Recruiting@usgllc811.com" className="text-skytec-primary hover:underline">Recruiting@usgllc811.com</a>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Locations */}
      <section className="py-24" data-testid="locations-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-skytec-fg mb-6 uppercase">
              WHERE WE'RE HIRING
            </h2>
            <p className="text-skytec-fg/60 max-w-2xl mx-auto mb-10">
              Join our teams across the Southeast and Midwest
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              {['Georgia', 'Tennessee', 'Illinois', 'Florida (HQ)'].map((state, index) => (
                <div 
                  key={state}
                  className="px-8 py-4 bg-skytec-surface rounded-xl border border-skytec-border"
                >
                  <MapPin className="w-6 h-6 text-skytec-primary mx-auto mb-2" />
                  <span className="font-heading font-bold text-skytec-fg">{state}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Recruiting */}
      <section className="py-16 bg-skytec-surface border-t border-skytec-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="font-heading text-2xl font-bold text-skytec-fg mb-4">
            Ready to Join USG?
          </h3>
          <p className="text-skytec-fg/60 mb-6">
            Contact our recruiting team with any questions about career opportunities.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="mailto:Recruiting@usgllc811.com">
              <Button 
                size="lg"
                className="bg-skytec-secondary text-white hover:bg-skytec-secondary/90 font-heading font-bold"
              >
                <Mail className="w-5 h-5 mr-2" />
                Recruiting@usgllc811.com
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
